import {
  PURCHASE_CONTRACTS_STATE_DEFAULTS,
  PurchaseContractsListState,
} from '@app/routes/index/purchase-contracts/index/_state';
import {
  SALES_CONTRACTS_STATE_DEFAULTS,
  SalesContractsListState,
} from '@app/routes/index/sales-contracts/_state';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import {
  LOCAL_STORAGE_ENGINE,
  SESSION_STORAGE_ENGINE,
  withNgxsStoragePlugin,
} from '@ngxs/storage-plugin';
import {
  ActionType,
  getActionTypeFromInstance,
  NgxsNextPluginFn,
  provideStore,
  withNgxsPlugin,
} from '@ngxs/store';
import {
  STOCK_TRANSACTIONS_STATE_DEFAULTS,
  StockTransactionsListState,
} from '@app/routes/index/stocks/index/_state';
import {
  AUTH_STATE_DEFAULTS,
  AUTH_STATE_NAME,
  AuthState,
} from '@app/infrastructure/authentication/auth.state';
import { ClearAuthState } from '@app/infrastructure/authentication/auth.actions';
import {
  DRAWINGS_LIST_STATE_DEFAULTS,
  DrawingsListState,
} from '@app/routes/index/drawings/index/_state';
import {
  DRAWINGS_PENDING_HAULAGE_LIST_STATE_DEFAULTS,
  DrawingsPendingHaulageListState,
} from '@app/routes/index/drawings-pending-haulage/index/_state';
import {
  RENT_INVOICING_STATE_DEFAULTS,
  RentInvoicingListState,
} from '@app/routes/index/rent-invoicing/_state';
import {
  CONTRACTS_DUE_FOR_INVOICING_STATE_DEFAULTS,
  ContractsDueForInvoicingListState,
} from '@app/routes/index/contracts-due-for-invoicing/index/_state';
import { POST_BATCH_STATE_DEFAULTS, PostBatchListState } from '@app/routes/index/post-batch/_state';
import {
  ALL_POSITIONS_LIST_STATE_DEFAULTS,
  AllPositionsListState,
} from '@app/routes/index/all-positions/index/_state';
import {
  SPOT_POSITIONS_LIST_STATE_DEFAULTS,
  SpotPositionsListState,
} from '@app/routes/index/spot-positions/index/_state';
import {
  OVERDRAWN_CONTRACTS_LIST_STATE_DEFAULTS,
  OverdrawnContractsListState,
} from '@app/routes/index/stock-positions-overview/overdrawn-contracts/_state';
import {
  UNMATCHED_PENDING_DRAWINGS_LIST_STATE_DEFAULTS,
  UnmatchedPendingDrawingsListState,
} from '@app/routes/index/stock-positions-overview/unmatched-pending-drawings/_state';
import {
  STORE_MANAGER_LIST_STATE_DEFAULTS,
  StoreManagerListState,
} from '@app/routes/index/store-manager/index/_state';
import {
  DRAWING_CANDIDATES_LIST_STATE_DEFAULTS,
  DrawingCandidatesListState,
} from '@app/routes/index/pending-drawings/index/drawings/_state';
import {
  IMPORT_FILES_LIST_STATE_DEFAULTS,
  ImportFilesListState,
} from '@app/routes/index/pending-drawings/index/files/_state';
import {
  SHIPPING_SCHEDULE_STATE_DEFAULTS,
  ShippingScheduleListState,
} from '@app/routes/index/shipping-schedule/_state';
import {
  FUTURES_CONTRACTS_STATE_DEFAULTS,
  FuturesContractsListState,
} from '@app/routes/index/futures-contracts/index/_state';
import {
  CLOSED_POSITIONS_STATE_DEFAULTS,
  ClosedPositionsListState,
} from '@app/routes/index/futures-closed-positions/index/_state';

type StateClass = Record<string, object | null | undefined>;

const STORAGE_STATES = [
  {
    state: AuthState,
    defaults: AUTH_STATE_DEFAULTS,
    stateName: AUTH_STATE_NAME,
  },
  {
    state: PurchaseContractsListState,
    defaults: PURCHASE_CONTRACTS_STATE_DEFAULTS,
    stateName: 'PurchaseContractsList',
  },
  {
    state: SalesContractsListState,
    defaults: SALES_CONTRACTS_STATE_DEFAULTS,
    stateName: 'SalesContractsList',
  },
  {
    state: FuturesContractsListState,
    defaults: FUTURES_CONTRACTS_STATE_DEFAULTS,
    stateName: 'FuturesContractsList',
  },
  {
    state: ClosedPositionsListState,
    defaults: CLOSED_POSITIONS_STATE_DEFAULTS,
    stateName: 'ClosedPositionsList',
  },
  {
    state: ShippingScheduleListState,
    defaults: SHIPPING_SCHEDULE_STATE_DEFAULTS,
    stateName: 'ShippingScheduleList',
  },
  {
    state: StockTransactionsListState,
    defaults: STOCK_TRANSACTIONS_STATE_DEFAULTS,
    stateName: 'StockTransactionsList',
  },
  {
    state: DrawingsListState,
    defaults: DRAWINGS_LIST_STATE_DEFAULTS,
    stateName: 'DrawingsList',
  },
  {
    state: DrawingsPendingHaulageListState,
    defaults: DRAWINGS_PENDING_HAULAGE_LIST_STATE_DEFAULTS,
    stateName: 'DrawingsPendingHaulageList',
  },
  {
    state: RentInvoicingListState,
    defaults: RENT_INVOICING_STATE_DEFAULTS,
    stateName: 'RentInvoicingList',
  },
  {
    state: ContractsDueForInvoicingListState,
    defaults: CONTRACTS_DUE_FOR_INVOICING_STATE_DEFAULTS,
    stateName: 'ContractsDueForInvoicingList',
  },
  {
    state: PostBatchListState,
    defaults: POST_BATCH_STATE_DEFAULTS,
    stateName: 'PostBatchList',
  },
  {
    state: SpotPositionsListState,
    defaults: SPOT_POSITIONS_LIST_STATE_DEFAULTS,
    stateName: 'SpotPositionsList',
  },
  {
    state: AllPositionsListState,
    defaults: ALL_POSITIONS_LIST_STATE_DEFAULTS,
    stateName: 'AllPositionsList',
  },
  {
    state: OverdrawnContractsListState,
    defaults: OVERDRAWN_CONTRACTS_LIST_STATE_DEFAULTS,
    stateName: 'OverdrawnContractsList',
  },
  {
    state: StoreManagerListState,
    defaults: STORE_MANAGER_LIST_STATE_DEFAULTS,
    stateName: 'StoreManagerList',
  },
  {
    state: ImportFilesListState,
    defaults: IMPORT_FILES_LIST_STATE_DEFAULTS,
    stateName: 'ImportFilesList',
  },
  {
    state: DrawingCandidatesListState,
    defaults: DRAWING_CANDIDATES_LIST_STATE_DEFAULTS,
    stateName: 'DrawingCandidatesList',
  },
  {
    state: UnmatchedPendingDrawingsListState,
    defaults: UNMATCHED_PENDING_DRAWINGS_LIST_STATE_DEFAULTS,
    stateName: 'UnmatchedPendingDrawingsList',
    engine: SESSION_STORAGE_ENGINE,
  },
];

export const provideNgxs = () =>
  provideStore(
    STORAGE_STATES.map((s) => s.state),
    withNgxsReduxDevtoolsPlugin(),
    withNgxsLoggerPlugin(),
    withNgxsPlugin(logoutPlugin),
    withNgxsStoragePlugin({
      keys: STORAGE_STATES.map((s) => ({
        key: s.stateName,
        engine: s.engine ?? LOCAL_STORAGE_ENGINE,
      })),
      migrations: STORAGE_STATES.map((s) => buildStorageMigrations(s.stateName, s.defaults)).flat(),
    }),
  );

function buildStorageMigrations<T extends { version: number }>(key: string, defaults: T) {
  return [
    {
      version: undefined!,
      key,
      migrate: () => defaults,
    },
    ...Array.from({ length: defaults.version }, (_, version) => ({
      version,
      key,
      migrate: () => defaults,
    })),
  ];
}

function logoutPlugin(state: StateClass, action: ActionType, next: NgxsNextPluginFn) {
  if (getActionTypeFromInstance(action) === ClearAuthState.type) {
    for (const key in state) {
      state[key] = STORAGE_STATES.find((s) => s.stateName === key)?.defaults || null;
    }
  }

  return next(state, action);
}
